import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select } from './ui.tsx';

/** Busca e filtro de situação das listas (guia visual: "Listas com busca e filtros"). */

export type StatusFilterValue = 'active' | 'inactive' | 'all';

export function matchesStatus(isActive: boolean, value: StatusFilterValue): boolean {
  return value === 'all' || isActive === (value === 'active');
}

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <Input
      type="search"
      className="w-full sm:w-72"
      value={value}
      placeholder={placeholder}
      aria-label={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Ativos, inativos ou todos, em botões que mostram qual está ligado (`aria-pressed`). */
export function StatusFilter({
  value,
  onChange,
}: {
  value: StatusFilterValue;
  onChange: (value: StatusFilterValue) => void;
}) {
  const { t } = useTranslation();
  const options: StatusFilterValue[] = ['active', 'inactive', 'all'];
  return (
    <div role="group" aria-label={t('fields.status')} className="flex flex-wrap gap-1">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          aria-pressed={value === o}
          onClick={() => onChange(o)}
          className={`min-h-8 rounded-full border px-3 text-xs ${
            value === o
              ? 'border-accent bg-brand-soft font-medium text-brand-text'
              : 'border-rule bg-sheet text-muted hover:text-ink'
          }`}
        >
          {t(`structure.statuses.${o}`)}
        </button>
      ))}
    </div>
  );
}

/**
 * Situação dos processos (chamados, ordens de serviço, pedidos: registros que andam por etapas,
 * sem ativo e inativo). Cada módulo diz em que grupo cai cada uma das suas etapas.
 */
export type ProcessGroup = 'open' | 'completed' | 'cancelled';
export type ProcessFilterValue = ProcessGroup | 'all';
export const PROCESS_FILTERS: readonly ProcessFilterValue[] = [
  'open',
  'completed',
  'cancelled',
  'all',
];

export function matchesProcess(group: ProcessGroup, value: ProcessFilterValue): boolean {
  return value === 'all' || group === value;
}

/** Em aberto (padrão), Concluídos, Cancelados, Todos, num `Select` curto (guia visual). */
export function ProcessStatusFilter({
  value,
  onChange,
  cancelledLabel,
}: {
  value: ProcessFilterValue;
  onChange: (value: ProcessFilterValue) => void;
  /** Quando o grupo junta mais de uma etapa (ex.: "Cancelados e rejeitados"). */
  cancelledLabel?: string;
}) {
  const { t } = useTranslation();
  return (
    <Select
      className="w-full sm:w-52"
      aria-label={t('fields.status')}
      value={value}
      onChange={(e) => onChange(e.target.value as ProcessFilterValue)}
    >
      {PROCESS_FILTERS.map((f) => (
        <option key={f} value={f}>
          {f === 'cancelled' && cancelledLabel ? cancelledLabel : t(`processFilters.${f}`)}
        </option>
      ))}
    </Select>
  );
}

/**
 * Linha de busca e filtros acima da lista, com a ação de criar na ponta direita (guia visual,
 * "Busca e filtros das listas").
 */
export function ListToolbar({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {children}
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}
